export type ConsentFormTemplate = {
  id: string
  home: string
  title: string
  body: string
  isArchived: boolean
  isMaster: boolean
  editedBy?: string
  editedOn?: string
  createdBy: string
  createdOn: string
  type?: ConsentType
  isCreated?: boolean
}

export type ConsentSendForm = {
  id?: string
  consentTemplate?: string
  title?: string
  isRefused?: boolean
}

export type CreateConsentFormsJSON = {
  resident: string
  nextReviewDate: string | null
  consents: ConsentSendForm[]
}

export type ConsentType = 'consent' | 'lackOfCapacity'

export enum ConsentStatus {
  noStatus = 'no-status',
  consentNotObtained = 'consent-not-obtained',
  completed = 'completed',
  planned = 'planned',
  requested = 'requested',
  scheduled = 'scheduled',
  postponed = 'postponed',
  cancelled = 'cancelled',
  withdrawn = 'withdrawn',
  refused = 'refused',
  provided = 'provided',
  pending = 'pending'
}

export type GroupedSelectOption = {
  label: string
  value: string
}

export type GroupedSelectOptions = {
  label: string
  options: GroupedSelectOption[]
}[]

export const SIGN_FORM_STATUSES: ConsentStatus[] = [
  ConsentStatus.cancelled,
  ConsentStatus.withdrawn,
  ConsentStatus.refused,
  ConsentStatus.provided
]

export const NEXT_REVIEW_DISABLED_STATUSES: ConsentStatus[] = [
  ConsentStatus.cancelled,
  ConsentStatus.consentNotObtained,
  ConsentStatus.withdrawn,
  ConsentStatus.completed,
  ConsentStatus.noStatus
]

export const NEXT_REVIEW_OPTIONAL_STATUSES: ConsentStatus[] = [
  ConsentStatus.refused,
  ConsentStatus.provided
]

export const getNextReviewFieldState = (status: ConsentStatus) => {
  const isDisabled = NEXT_REVIEW_DISABLED_STATUSES.includes(status)
  const isOptional = NEXT_REVIEW_OPTIONAL_STATUSES.includes(status)
  const isRequired = !isDisabled && !isOptional

  return { isDisabled, isOptional, isRequired }
}

export const getIsConsentConfirmDisabled = ({
  status,
  reason,
  disabled = false
}: {
  status: ConsentStatus
  reason?: string
  lastReviewDate?: string | null
  disabled?: boolean
}): boolean => {
  return (
    status === ConsentStatus.noStatus ||
    disabled ||
    (status === ConsentStatus.withdrawn && !reason?.trim())
  )
}

export const getIsSignStatus = (status: ConsentStatus): boolean => {
  return SIGN_FORM_STATUSES.includes(status)
}

/**
 * Ant Design Tag preset colors mapped to semantic status meanings.
 * - default (grey) → neutral/inactive states
 * - danger (red) → refused
 * - success (green) → provided
 */
export const TAG_COLORS = {
  default: 'default',
  danger: 'red',
  success: 'green'
} as const

export type ConsentStatusTagColor =
  | (typeof TAG_COLORS)[keyof typeof TAG_COLORS]
  | undefined

export const STATUS_TAG_COLORS: Partial<
  Record<ConsentStatus, ConsentStatusTagColor>
> = {
  [ConsentStatus.consentNotObtained]: TAG_COLORS.default,
  [ConsentStatus.cancelled]: TAG_COLORS.default,
  [ConsentStatus.withdrawn]: TAG_COLORS.default,
  [ConsentStatus.completed]: TAG_COLORS.default,
  [ConsentStatus.planned]: TAG_COLORS.default,
  [ConsentStatus.requested]: TAG_COLORS.default,
  [ConsentStatus.scheduled]: TAG_COLORS.default,
  [ConsentStatus.postponed]: TAG_COLORS.default,
  [ConsentStatus.refused]: TAG_COLORS.danger,
  [ConsentStatus.provided]: TAG_COLORS.success
}

export const getStatusTagColor = (
  status: ConsentStatus | string
): ConsentStatusTagColor => {
  return STATUS_TAG_COLORS[status as ConsentStatus]
}

export const STATUS_TRANSLATION_KEYS: Partial<Record<ConsentStatus, string>> = {
  [ConsentStatus.completed]: 'CONSENT_STATUS_COMPLETED',
  [ConsentStatus.pending]: 'CONSENT_STATUS_PENDING',
  [ConsentStatus.requested]: 'CONSENT_STATUS_REQUESTED',
  [ConsentStatus.refused]: 'CONSENT_STATUS_REFUSED',
  [ConsentStatus.withdrawn]: 'CONSENT_STATUS_WITHDRAWN',
  [ConsentStatus.planned]: 'CONSENT_STATUS_PLANNED',
  [ConsentStatus.scheduled]: 'CONSENT_STATUS_SCHEDULED',
  [ConsentStatus.postponed]: 'CONSENT_STATUS_POSTPONED',
  [ConsentStatus.cancelled]: 'CONSENT_STATUS_CANCELLED',
  [ConsentStatus.provided]: 'CONSENT_STATUS_PROVIDED'
}
